import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTAddressToSDKAddress } from '@wix/sdk-runtime/transformations/address';
import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformRESTImageToSDKImage } from '@wix/sdk-runtime/transformations/image';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  QuerySubscriptionContractsResponse,
  SubscriptionContract,
  SubscriptionContractCreatedEnvelope,
  SubscriptionContractDeletedEnvelope,
  SubscriptionContractQuery,
  SubscriptionContractUpdatedEnvelope,
  SubscriptionContractsQueryBuilder,
  getSubscriptionContract as universalGetSubscriptionContract,
  querySubscriptionContracts as universalQuerySubscriptionContracts,
  typedQuerySubscriptionContracts as universalTypedQuerySubscriptionContracts,
} from './ecom-subscription-contracts-v1-subscription-contract-subscription-contracts.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/ecom' };

export function getSubscriptionContract(
  httpClient: HttpClient
): GetSubscriptionContractSignature {
  return (subscriptionContractId: string) =>
    universalGetSubscriptionContract(
      subscriptionContractId,
      // @ts-ignore
      { httpClient }
    );
}

interface GetSubscriptionContractSignature {
  /**
   * Retrieves a SubscriptionContract.
   * @param - ID of the SubscriptionContract to retrieve.
   * @returns The requested SubscriptionContract.
   */
  (subscriptionContractId: string): Promise<
    NonNullablePaths<
      SubscriptionContract,
      | `subscriptionSettings.frequency`
      | `subscriptionSettings.autoRenewal`
      | `subscriptionSettings.enableCustomerCancellation`
      | `subscriptionSettings.freeTrialPeriod.frequency`
      | `subscriptionSettings.freeTrialPeriod.interval`
      | `lineItems`
      | `lineItems.${number}._id`
      | `lineItems.${number}.productName.original`
      | `lineItems.${number}.catalogReference.catalogItemId`
      | `lineItems.${number}.catalogReference.appId`
      | `lineItems.${number}.quantity`
      | `lineItems.${number}.totalDiscount.amount`
      | `lineItems.${number}.totalDiscount.formattedAmount`
      | `lineItems.${number}.physicalProperties.shippable`
      | `lineItems.${number}.itemType.preset`
      | `lineItems.${number}.itemType.custom`
      | `lineItems.${number}.paymentOption`
      | `lineItems.${number}.taxDetails.taxRate`
      | `lineItems.${number}.taxInfo.taxIncludedInPrice`
      | `lineItems.${number}.digitalFile.fileId`
      | `lineItems.${number}.subscriptionInfo.cycleNumber`
      | `lineItems.${number}.subscriptionInfo.subscriptionOptionTitle`
      | `lineItems.${number}.subscriptionInfo.subscriptionSettings.frequency`
      | `lineItems.${number}.subscriptionInfo.subscriptionSettings.autoRenewal`
      | `lineItems.${number}.subscriptionInfo.subscriptionSettings.enableCustomerCancellation`
      | `lineItems.${number}.priceDescription.original`
      | `lineItems.${number}.taxableAddress.addressType`
      | `lineItems.${number}.priceUndetermined`
      | `lineItems.${number}.fixedQuantity`
      | `lineItems.${number}.decimalQuantity.measurementUnit`
      | `lineItems.${number}.decimalQuantity.otherUnit`
      | `lineItems.${number}.decimalQuantity.value`
      | `lineItems.${number}.decimalQuantity.description.original`
      | `buyerInfo.visitorId`
      | `buyerInfo.memberId`
      | `weightUnit`
      | `taxIncludedInPrices`
      | `platformFeeSummary.fees`
      | `platformFeeSummary.fees.${number}.lineItemId`
      | `platformFeeSummary.fees.${number}.chargeType`
      | `platformFeeSummary.fees.${number}.percentageRate`
      | `billingInfo.address.streetAddress.number`
      | `billingInfo.address.streetAddress.name`
      | `billingInfo.contactDetails.vatId._id`
      | `billingInfo.contactDetails.vatId.type`
      | `shippingInfo.title`
      | `shippingInfo.logistics.pickupDetails.pickupMethod`
      | `appliedDiscounts`
      | `appliedDiscounts.${number}.coupon._id`
      | `appliedDiscounts.${number}.coupon.code`
      | `appliedDiscounts.${number}.coupon.name`
      | `appliedDiscounts.${number}.merchantDiscount.discountReason`
      | `appliedDiscounts.${number}.discountRule._id`
      | `appliedDiscounts.${number}.discountRule.name.original`
      | `appliedDiscounts.${number}.discountType`
      | `channelInfo.type`
      | `customFields`
      | `customFields.${number}.title`
      | `additionalFees`
      | `additionalFees.${number}.name`
      | `additionalFees.${number}._id`
      | `additionalFees.${number}.source`
      | `activities`
      | `activities.${number}.changesApplied.draftSubscriptionContractId`
      | `activities.${number}._id`
      | `activities.${number}.author.userId`
      | `activities.${number}.author.appId`
      | `activities.${number}.activityType`,
      6
    >
  >;
}

export function querySubscriptionContracts(
  httpClient: HttpClient
): QuerySubscriptionContractsSignature {
  return () =>
    universalQuerySubscriptionContracts(
      // @ts-ignore
      { httpClient }
    );
}

interface QuerySubscriptionContractsSignature {
  /**
   * Retrieves a list of SubscriptionContracts, given the provided [paging, filtering, and sorting][1].
   *
   * Up to 300 SubscriptionContracts can be returned per request.
   *
   * To learn how to query SubscriptionContracts, see [API Query Language][2].
   *
   * [1]: https://dev.wix.com/api/rest/getting-started/sorting-and-paging
   * [2]: https://dev.wix.com/api/rest/getting-started/api-query-language
   */
  (): SubscriptionContractsQueryBuilder;
}

export function typedQuerySubscriptionContracts(
  httpClient: HttpClient
): TypedQuerySubscriptionContractsSignature {
  return (query: SubscriptionContractQuery) =>
    universalTypedQuerySubscriptionContracts(
      query,
      // @ts-ignore
      { httpClient }
    );
}

interface TypedQuerySubscriptionContractsSignature {
  /** */
  (query: SubscriptionContractQuery): Promise<
    NonNullablePaths<
      QuerySubscriptionContractsResponse,
      | `subscriptionContracts`
      | `subscriptionContracts.${number}.subscriptionSettings.frequency`
      | `subscriptionContracts.${number}.subscriptionSettings.autoRenewal`
      | `subscriptionContracts.${number}.subscriptionSettings.enableCustomerCancellation`
      | `subscriptionContracts.${number}.subscriptionSettings.freeTrialPeriod.frequency`
      | `subscriptionContracts.${number}.subscriptionSettings.freeTrialPeriod.interval`
      | `subscriptionContracts.${number}.buyerInfo.visitorId`
      | `subscriptionContracts.${number}.buyerInfo.memberId`
      | `subscriptionContracts.${number}.weightUnit`
      | `subscriptionContracts.${number}.taxIncludedInPrices`
      | `subscriptionContracts.${number}.shippingInfo.title`
      | `subscriptionContracts.${number}.channelInfo.type`,
      6
    >
  >;
}

export const onSubscriptionContractCreated = /*#__PURE__*/ EventDefinition(
  'wix.ecom.subscription_contracts.v1.subscription_contract_created',
  true,
  (event: SubscriptionContractCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'entity.subscriptionSettings.startDate' },
            { path: 'entity.lineItems.digitalFile.expirationDate' },
            {
              path: 'entity.lineItems.subscriptionInfo.subscriptionSettings.startDate',
            },
            { path: 'entity.lineItems.serviceProperties.scheduledDate' },
            { path: 'entity.lineItems.serviceProperties.endDate' },
            { path: 'entity.shippingInfo.logistics.deliverByDate' },
            { path: 'entity.shippingInfo.logistics.deliveryTimeSlot.from' },
            { path: 'entity.shippingInfo.logistics.deliveryTimeSlot.to' },
            { path: 'entity.activities.createdDate' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'entity.lineItems.image' }],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [{ path: 'entity.lineItems.physicalProperties.weight' }],
        },
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [
            { path: 'entity.billingInfo.address' },
            {
              path: 'entity.shippingInfo.logistics.shippingDestination.address',
            },
            { path: 'entity.shippingInfo.logistics.pickupDetails.address' },
            { path: 'entity.recipientInfo.address' },
          ],
        },
      ])
    )
)<SubscriptionContractCreatedEnvelope>();
export const onSubscriptionContractDeleted = /*#__PURE__*/ EventDefinition(
  'wix.ecom.subscription_contracts.v1.subscription_contract_deleted',
  true,
  (event: SubscriptionContractDeletedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'undefined.createdDate' },
            { path: 'undefined.updatedDate' },
            { path: 'undefined.subscriptionSettings.startDate' },
            { path: 'undefined.lineItems.digitalFile.expirationDate' },
            {
              path: 'undefined.lineItems.subscriptionInfo.subscriptionSettings.startDate',
            },
            { path: 'undefined.lineItems.serviceProperties.scheduledDate' },
            { path: 'undefined.lineItems.serviceProperties.endDate' },
            { path: 'undefined.shippingInfo.logistics.deliverByDate' },
            { path: 'undefined.shippingInfo.logistics.deliveryTimeSlot.from' },
            { path: 'undefined.shippingInfo.logistics.deliveryTimeSlot.to' },
            { path: 'undefined.activities.createdDate' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'undefined.lineItems.image' }],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [{ path: 'undefined.lineItems.physicalProperties.weight' }],
        },
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [
            { path: 'undefined.billingInfo.address' },
            {
              path: 'undefined.shippingInfo.logistics.shippingDestination.address',
            },
            { path: 'undefined.shippingInfo.logistics.pickupDetails.address' },
            { path: 'undefined.recipientInfo.address' },
          ],
        },
      ])
    )
)<SubscriptionContractDeletedEnvelope>();
export const onSubscriptionContractUpdated = /*#__PURE__*/ EventDefinition(
  'wix.ecom.subscription_contracts.v1.subscription_contract_updated',
  true,
  (event: SubscriptionContractUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'entity.subscriptionSettings.startDate' },
            { path: 'entity.lineItems.digitalFile.expirationDate' },
            {
              path: 'entity.lineItems.subscriptionInfo.subscriptionSettings.startDate',
            },
            { path: 'entity.lineItems.serviceProperties.scheduledDate' },
            { path: 'entity.lineItems.serviceProperties.endDate' },
            { path: 'entity.shippingInfo.logistics.deliverByDate' },
            { path: 'entity.shippingInfo.logistics.deliveryTimeSlot.from' },
            { path: 'entity.shippingInfo.logistics.deliveryTimeSlot.to' },
            { path: 'entity.activities.createdDate' },
            { path: 'metadata.eventTime' },
            { path: 'modifiedFields.createdDate' },
            { path: 'modifiedFields.updatedDate' },
            { path: 'modifiedFields.subscriptionSettings.startDate' },
            { path: 'modifiedFields.lineItems.digitalFile.expirationDate' },
            {
              path: 'modifiedFields.lineItems.subscriptionInfo.subscriptionSettings.startDate',
            },
            {
              path: 'modifiedFields.lineItems.serviceProperties.scheduledDate',
            },
            { path: 'modifiedFields.lineItems.serviceProperties.endDate' },
            { path: 'modifiedFields.shippingInfo.logistics.deliverByDate' },
            {
              path: 'modifiedFields.shippingInfo.logistics.deliveryTimeSlot.from',
            },
            {
              path: 'modifiedFields.shippingInfo.logistics.deliveryTimeSlot.to',
            },
            { path: 'modifiedFields.activities.createdDate' },
          ],
        },
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'entity.lineItems.image' },
            { path: 'modifiedFields.lineItems.image' },
          ],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [
            { path: 'entity.lineItems.physicalProperties.weight' },
            { path: 'modifiedFields.lineItems.physicalProperties.weight' },
          ],
        },
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [
            { path: 'entity.billingInfo.address' },
            {
              path: 'entity.shippingInfo.logistics.shippingDestination.address',
            },
            { path: 'entity.shippingInfo.logistics.pickupDetails.address' },
            { path: 'entity.recipientInfo.address' },
            { path: 'modifiedFields.billingInfo.address' },
            {
              path: 'modifiedFields.shippingInfo.logistics.shippingDestination.address',
            },
            {
              path: 'modifiedFields.shippingInfo.logistics.pickupDetails.address',
            },
            { path: 'modifiedFields.recipientInfo.address' },
          ],
        },
      ])
    )
)<SubscriptionContractUpdatedEnvelope>();

export {
  AccountInfo,
  AccountInfoMetadata,
  ActionEvent,
  ActionInitiator,
  ActivityAuthor,
  ActivityAuthorValueOneOf,
  ActivityType,
  AdditionalFee,
  AdditionalFeeDelta,
  AdditionalFeeDeltaDeltaDetailsOneOf,
  AdditionalFeeSource,
  Address,
  AddressLocation,
  AddressWithContact,
  AdjustmentType,
  ApplicationError,
  AppliedDiscount,
  AppliedDiscountDelta,
  AppliedDiscountDeltaDeltaDetailsOneOf,
  AppliedDiscountDiscountSourceOneOf,
  BaseEventMetadata,
  BillingAdjustment,
  BillingAdjustmentPriceSummary,
  BillingSettings,
  BulkActionMetadata,
  BulkCreateSubscriptionContractsRequest,
  BulkCreateSubscriptionContractsResponse,
  BulkSubscriptionContractResult,
  BulkUpdateSubscriptionContractsRequest,
  BulkUpdateSubscriptionContractsResponse,
  BuyerInfo,
  BuyerInfoIdOneOf,
  CatalogReference,
  ChangeType,
  ChangesAppliedActivity,
  ChangesAppliedActivityTargetOneOf,
  ChannelInfo,
  ChannelType,
  ChargeType,
  Color,
  CommitSubscriptionContractChangesRequest,
  CommitSubscriptionContractChangesResponse,
  ContractChange,
  ContractChangeValueOneOf,
  ContractTarget,
  Coupon,
  CreateSubscriptionContractRequest,
  CreateSubscriptionContractResponse,
  CurrencyConversionDetails,
  CursorPaging,
  CursorPagingMetadata,
  CursorQuery,
  CursorQueryPagingMethodOneOf,
  Cursors,
  CustomField,
  DecimalQuantity,
  DecimalQuantityUnitOneOf,
  DeliveryLogistics,
  DeliveryLogisticsAddressOneOf,
  DeliveryTimeSlot,
  DescriptionLine,
  DescriptionLineDescriptionLineValueOneOf,
  DescriptionLineName,
  DescriptionLineType,
  DescriptionLineValueOneOf,
  DigitalFile,
  Dimensions,
  DimensionsUnit,
  DiscountReason,
  DiscountRule,
  DiscountRuleName,
  DiscountType,
  DomainEvent,
  DomainEventBodyOneOf,
  Empty,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EventMetadata,
  ExtendedFields,
  FocalPoint,
  FreeTrialPeriod,
  FullAddressContactDetails,
  GetSubscriptionContractRequest,
  GetSubscriptionContractResponse,
  IdentificationData,
  IdentificationDataIdOneOf,
  ItemCombination,
  ItemCombinationLineItem,
  ItemMetadata,
  ItemModifier,
  ItemTaxFullDetails,
  ItemType,
  ItemTypeItemTypeDataOneOf,
  ItemTypePreset,
  JurisdictionType,
  LineItemChanges,
  LineItemDelta,
  LineItemDiscount,
  LineItemQuantityChange,
  LineItemQuantityChangeType,
  LineItemTaxBreakdown,
  LineItemTaxInfo,
  Location,
  LocationAndQuantity,
  ManagedAdditionalFee,
  ManagedDiscount,
  ManagedLineItem,
  MaskedSubscriptionContract,
  MeasurementUnit,
  MerchantDiscount,
  MerchantDiscountMerchantDiscountReasonOneOf,
  MessageEnvelope,
  ModifierGroup,
  OrderLineItem,
  OrderTaxBreakdown,
  OrderTaxInfo,
  PaymentOptionType,
  PhysicalProperties,
  PickupAddress,
  PickupDetails,
  PickupMethod,
  PlainTextValue,
  PlatformFee,
  PlatformFeeSummary,
  Price,
  PriceDescription,
  PriceSummary,
  ProductName,
  ProductPeriodAlignment,
  QuerySubscriptionContractsRequest,
  QuerySubscriptionContractsResponse,
  RestockLocation,
  RestoreInfo,
  ServiceProperties,
  ShippingInformation,
  ShippingPrice,
  ShippingRegion,
  SortOrder,
  Sorting,
  SpecificItemsCouponBehavior,
  StreetAddress,
  SubscriptionCommitSettings,
  SubscriptionContract,
  SubscriptionContractActivity,
  SubscriptionContractActivityContentOneOf,
  SubscriptionContractActivityTargetOneOf,
  SubscriptionContractCanceled,
  SubscriptionContractChangesCommitted,
  SubscriptionContractCreatedEnvelope,
  SubscriptionContractDeletedEnvelope,
  SubscriptionContractDiffs,
  SubscriptionContractExpired,
  SubscriptionContractQuerySpec,
  SubscriptionContractSettings,
  SubscriptionContractUpdatedEnvelope,
  SubscriptionContractsQueryBuilder,
  SubscriptionContractsQueryResult,
  SubscriptionDescription,
  SubscriptionFrequency,
  SubscriptionInfo,
  SubscriptionSettings,
  SubscriptionSettingsProductPeriodAlignment,
  SubscriptionTitle,
  TaxableAddress,
  TaxableAddressTaxableAddressDataOneOf,
  TaxableAddressType,
  TotalPriceChange,
  TranslatableString,
  TranslatedValue,
  UpdateSubscriptionContractRequest,
  UpdateSubscriptionContractResponse,
  V1BillingSettings,
  V1FreeTrialPeriod,
  V1SubscriptionDescription,
  V1SubscriptionSettings,
  V1SubscriptionTitle,
  V2SubscriptionFrequency,
  VatId,
  VatType,
  WebhookIdentityType,
  WeightUnit,
  utils,
} from './ecom-subscription-contracts-v1-subscription-contract-subscription-contracts.universal.js';
